import React, { type FC, Fragment, useCallback, useMemo, useState } from "react";
import { shallowEqual } from "react-redux";
import styles from "./Summary.module.scss";
import Text from "../../../../../../shared-components/css-modules/toyota/atoms/Text";
import Collapse from "../../../../../../shared-components/css-modules/toyota/molecules/Collapse";
import Popover from "../../../../../../shared-components/css-modules/toyota/vendor/popover/Popover";
import Button from "../../../../../../shared-components/css-modules/toyota/atoms/Button";
import Icon from "../../../../../../shared-components/css-modules/toyota/atoms/Icon";
import a11y from "../../../../../../shared-components/css-modules/toyota/utils/Accessibility.module.scss";
import type { SortedSpecType } from "../../../../../../../../common-deprecated/types/SpecTypes";
import { getWLTPLabel } from "../../../../../../../../common-deprecated/settings/utils/commonSettingUtils";
import { selectMotorization } from "../../../../../redux/slices/CarConfigMotorizationSlice";
import { useCarConfigSelector } from "../../../../../redux/store";
import { CarConfigLabel } from "../../../../../utils/labelUtils";
import { trackOrderOverviewSpecsPanelOpen } from "../../../../../utils/tracking";
import { selectCarMotorization } from "../../../../../redux/slices/CarConfigCarSlice";

type EcoSpecsProps = {
    ecoSpecs: SortedSpecType[] | null;
    alternateFlow?: boolean;
};
export const SummaryEcoSpecs: FC<EcoSpecsProps> = (props): JSX.Element | null => {
    const { ecoSpecs, alternateFlow } = props;

    const commonSettings = useCarConfigSelector((state) => state.commonSettings);
    const motorizations = useCarConfigSelector(selectMotorization, shallowEqual);
    const currentMotorization = useCarConfigSelector(selectCarMotorization, shallowEqual);

    const [ecoSpecsIsOpen, setEcoSpecsIsOpen] = useState(alternateFlow);

    const fullCurrentMotorization = motorizations.find(
        (motorization) =>
            currentMotorization.engine.id === motorization.engine.id &&
            currentMotorization.wheeldrive.id === motorization.wheeldrive.id &&
            currentMotorization.transmission.id === motorization.transmission.id,
    );
    const wltpLabel = fullCurrentMotorization
        ? getWLTPLabel(
              commonSettings,
              fullCurrentMotorization.engine.certification,
              fullCurrentMotorization.engine.type.electric,
          )
        : undefined;

    const handleClick = useCallback((event: React.MouseEvent, changeOpenState: boolean) => {
        event.stopPropagation();
        event.preventDefault();
        if (changeOpenState) setEcoSpecsIsOpen((oldIsOpen) => !oldIsOpen);
    }, []);

    return useMemo(() => {
        if (!ecoSpecs?.length) return null;
        return (
            <Collapse
                onClick={(event) => handleClick(event, true)}
                open={ecoSpecsIsOpen}
                summary={
                    <Text tag="h3" variant="apheleia-h5">
                        {ecoSpecs[0]?.title}
                        {wltpLabel && (
                            <Popover placement="top">
                                <Popover.Trigger>
                                    <Button
                                        variant="clean"
                                        onClick={(event) => {
                                            event.stopPropagation();
                                        }}
                                    >
                                        <Icon variant="info" sizing="md" aria-hidden="true" />
                                        <span className={a11y["sr-only"]}>
                                            <CarConfigLabel label="carConfigA11yButtonToggleCo2Info" />
                                        </span>
                                    </Button>
                                </Popover.Trigger>
                                <Popover.Content onClick={(event) => handleClick(event, false)}>
                                    <div dangerouslySetInnerHTML={{ __html: wltpLabel }} />
                                </Popover.Content>
                            </Popover>
                        )}
                    </Text>
                }
                onChange={(isOpen) => {
                    trackOrderOverviewSpecsPanelOpen(!!isOpen, "eco-specs");
                }}
            >
                <ul role="list" className={styles["config-sublist"]}>
                    {ecoSpecs.map((ecoSpec, ecoIndex) => (
                        <Fragment key={ecoSpec.id}>
                            {ecoIndex !== 0 && ecoSpec.title && (
                                <li className={styles["config-subitem-title"]}>
                                    <Text tag="h2" variant="apheleia-h4" className={styles.subtitle}>
                                        {ecoSpec.title}
                                    </Text>
                                </li>
                            )}
                            {!!ecoSpec.data &&
                                ecoSpec.data.map((spec) => (
                                    <li className={styles["config-subitem"]} key={spec.id}>
                                        <div className={styles["config-subitem-title"]}>
                                            <Text tag="h4" variant="apheleia-body">
                                                {spec.name}
                                            </Text>
                                        </div>
                                        <Text variant="apheleia-body" className={styles["config-subitem-price"]}>
                                            {`${spec.value} ${spec.unit}`}
                                            {spec.tooltip && (
                                                <Popover placement="top">
                                                    <Popover.Trigger onClick={(event) => handleClick(event, false)}>
                                                        <Button variant="clean">
                                                            <Icon variant="info" sizing="md" aria-hidden="true" />
                                                            <span className={a11y["sr-only"]}>
                                                                <CarConfigLabel label="carConfigA11yButtonToggleCo2Info" />
                                                            </span>
                                                        </Button>
                                                    </Popover.Trigger>
                                                    <Popover.Content onClick={(event) => handleClick(event, false)}>
                                                        <div dangerouslySetInnerHTML={{ __html: spec.tooltip }} />
                                                    </Popover.Content>
                                                </Popover>
                                            )}
                                        </Text>
                                    </li>
                                ))}
                        </Fragment>
                    ))}
                </ul>
            </Collapse>
        );
    }, [currentMotorization, ecoSpecsIsOpen, ecoSpecs]);
};
